import { UNIFORM_STALE_CACHE_TTL_MS } from '@/core/constants/fetch-resilience';

type StaleCacheEntry<T> = {
  expiresAt: number;
  value: T;
};

const GLOBAL_STALE_CACHE_KEY = '__mindoasisUniformStaleCache';

function getCacheStore(): Map<string, StaleCacheEntry<unknown>> {
  const globalScope = globalThis as typeof globalThis & {
    [GLOBAL_STALE_CACHE_KEY]?: Map<string, StaleCacheEntry<unknown>>;
  };

  if (!globalScope[GLOBAL_STALE_CACHE_KEY]) {
    globalScope[GLOBAL_STALE_CACHE_KEY] = new Map();
  }

  return globalScope[GLOBAL_STALE_CACHE_KEY];
}

export function getStaleUniformCache<T>(key: string): T | undefined {
  const entry = getCacheStore().get(key);
  if (!entry) {
    return undefined;
  }

  if (Date.now() > entry.expiresAt) {
    getCacheStore().delete(key);
    return undefined;
  }

  return entry.value as T;
}

export function setStaleUniformCache<T>(key: string, value: T): void {
  getCacheStore().set(key, {
    value,
    expiresAt: Date.now() + UNIFORM_STALE_CACHE_TTL_MS,
  });
}

export async function withStaleUniformFallback<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
): Promise<T> {
  try {
    const value = await fetchFn();
    setStaleUniformCache(cacheKey, value);
    return value;
  } catch (error) {
    const staleValue = getStaleUniformCache<T>(cacheKey);
    if (staleValue !== undefined) {
      console.warn(
        JSON.stringify({
          code: 'UNIFORM_STALE_CACHE_HIT',
          cacheKey,
          message: error instanceof Error ? error.message : String(error),
        }),
      );
      return staleValue;
    }

    throw error;
  }
}
